import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  FileText, User, Clock, ChevronLeft, ChevronRight,
  Archive, RotateCcw, Trash2, Plus, CheckCircle2, XCircle, Phone,
  Building2, RefreshCw, Activity, Download
} from 'lucide-react';
import { MainLayout } from '../components/MainLayout';
import { toast } from 'sonner';

const ACTION_ICONS = {
  enquiry_created: { icon: Plus, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  enquiry_deleted: { icon: Archive, color: 'text-amber-600', bg: 'bg-amber-50' },
  enquiry_restored: { icon: RotateCcw, color: 'text-blue-600', bg: 'bg-blue-50' },
  enquiry_permanent_deleted: { icon: Trash2, color: 'text-red-600', bg: 'bg-red-50' },
  enquiry_bulk_archived: { icon: Archive, color: 'text-amber-600', bg: 'bg-amber-50' },
  enquiry_bulk_restored: { icon: RotateCcw, color: 'text-blue-600', bg: 'bg-blue-50' },
  enquiry_bulk_permanent_deleted: { icon: Trash2, color: 'text-red-600', bg: 'bg-red-50' },
  decision_made: { icon: CheckCircle2, color: 'text-violet-600', bg: 'bg-violet-50' },
  quote_created: { icon: FileText, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  quote_approved: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50' },
  quote_rejected: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
  contact_created: { icon: Building2, color: 'text-sky-600', bg: 'bg-sky-50' },
  outreach_logged: { icon: Phone, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  task_completed: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
};

const ACTION_LABELS = {
  enquiry_created: 'Enquiry Created',
  enquiry_deleted: 'Enquiry Archived',
  enquiry_restored: 'Enquiry Restored',
  enquiry_permanent_deleted: 'Enquiry Permanently Deleted',
  enquiry_bulk_archived: 'Bulk Archive',
  enquiry_bulk_restored: 'Bulk Restore',
  enquiry_bulk_permanent_deleted: 'Bulk Permanent Delete',
  decision_made: 'Decision Made',
  quote_created: 'Quote Created',
  quote_approved: 'Quote Approved',
  quote_rejected: 'Quote Rejected',
  contact_created: 'Contact Created',
  outreach_logged: 'Outreach Logged',
  task_completed: 'Task Completed',
};

export const AuditLogsPage = () => {
  const { user, authFetch } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    action_type: 'all',
    user_id: 'all',
    date_from: '',
    date_to: '',
  });
  const [filterOptions, setFilterOptions] = useState({
    action_types: [],
    users: []
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0
  });

  // Redirect non-directors
  useEffect(() => {
    if (user && user.role !== 'director') {
      navigate('/');
      toast.error('Director access required');
    }
  }, [user, navigate]);

  useEffect(() => {
    loadLogs();
    loadSummary();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const exportToCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.action_type !== 'all') params.append('action_type', filters.action_type);
      if (filters.user_id !== 'all') params.append('user_id', filters.user_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const res = await authFetch(`/audit-logs/export-csv?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        
        // Create blob and download
        const blob = new Blob([data.csv_content], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = data.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast.success(`Exported ${data.total_records} records to CSV`);
      }
    } catch (err) {
      toast.error('Failed to export CSV');
    }
  };

  const loadLogs = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.action_type !== 'all') params.append('action_type', filters.action_type);
      if (filters.user_id !== 'all') params.append('user_id', filters.user_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      params.append('skip', ((page - 1) * 50).toString());
      params.append('limit', '50');
      
      const res = await authFetch(`/audit-logs?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setPagination({
          page: data.page || 1,
          pages: data.pages || 1,
          total: data.total || 0
        });
        if (data.filters) {
          setFilterOptions({
            action_types: data.filters.action_types || [],
            users: data.filters.users || []
          });
        }
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const res = await authFetch('/audit-logs/summary?days=30');
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Summary error:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionDisplay = (action) => {
    const config = ACTION_ICONS[action] || { icon: Activity, color: 'text-slate-600', bg: 'bg-slate-50' };
    const Icon = config.icon;
    return { Icon, ...config };
  };

  if (user?.role !== 'director') {
    return null;
  }

  return (
    <MainLayout>
      <div className="space-y-6 max-w-full overflow-x-hidden" data-testid="audit-logs-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-2xl flex items-center justify-center shadow-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#1e3a5f]">
                  Audit Logs
                </h1>
                <p className="text-slate-500">
                  {pagination.total} total actions • Director access only
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={exportToCSV}
              className="rounded-full"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => { loadLogs(); loadSummary(); }}
              className="rounded-full"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/enquiries/archived')}
              className="rounded-full"
            >
              <Archive className="w-4 h-4 mr-2" />
              Archived Enquiries
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-slate-400" />
                <span className="text-xs font-medium text-slate-500">Total Actions (30d)</span>
              </div>
              <p className="text-2xl font-bold text-[#1e3a5f]">{summary.total_actions}</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Archive className="w-4 h-4 text-amber-500" />
                <span className="text-xs font-medium text-slate-500">Archived</span>
              </div>
              <p className="text-2xl font-bold text-[#1e3a5f]">
                {(summary.action_counts?.enquiry_deleted || 0) + 
                 (summary.action_counts?.enquiry_bulk_archived || 0)}
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <RotateCcw className="w-4 h-4 text-blue-500" />
                <span className="text-xs font-medium text-slate-500">Restored</span>
              </div>
              <p className="text-2xl font-bold text-[#1e3a5f]">
                {(summary.action_counts?.enquiry_restored || 0) + 
                 (summary.action_counts?.enquiry_bulk_restored || 0)}
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Trash2 className="w-4 h-4 text-red-500" />
                <span className="text-xs font-medium text-slate-500">Permanently Deleted</span>
              </div>
              <p className="text-2xl font-bold text-[#1e3a5f]">
                {(summary.action_counts?.enquiry_permanent_deleted || 0) + 
                 (summary.action_counts?.enquiry_bulk_permanent_deleted || 0)}
              </p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Action Type</label>
              <Select
                value={filters.action_type}
                onValueChange={(value) => setFilters(prev => ({ ...prev, action_type: value }))}
              >
                <SelectTrigger className="rounded-xl" data-testid="filter-action">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {filterOptions.action_types.map(type => (
                    <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">User</label>
              <Select
                value={filters.user_id}
                onValueChange={(value) => setFilters(prev => ({ ...prev, user_id: value }))}
              >
                <SelectTrigger className="rounded-xl" data-testid="filter-user">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {filterOptions.users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.first_name} {u.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">From Date</label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                className="rounded-xl"
                data-testid="filter-date-from"
              />
            </div>
            
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">To Date</label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                className="rounded-xl"
                data-testid="filter-date-to"
              />
            </div>
          </div>
        </div>

        {/* Logs List */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-pulse text-slate-500">Loading audit logs...</div>
            </div>
          ) : logs.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {logs.map((log, idx) => {
                const { Icon, color, bg } = getActionDisplay(log.action);
                return (
                  <div 
                    key={log.id || idx} 
                    className="p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`log-${idx}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-5 h-5 ${color}`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-[#1e3a5f]">
                            {ACTION_LABELS[log.action] || log.action}
                          </span>
                          {log.entity_type && (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
                              {log.entity_type}
                            </span>
                          )}
                        </div>
                        
                        {/* Details */}
                        {log.details && (
                          <div className="mt-1 text-sm text-slate-600">
                            {log.details.project_name && (
                              <span>Project: <strong>{log.details.project_name}</strong></span>
                            )}
                            {log.details.reference_number && (
                              <span className="ml-2 font-mono text-xs text-slate-500">
                                ({log.details.reference_number})
                              </span>
                            )}
                            {log.details.reason && (
                              <span className="block mt-1 text-slate-500">
                                Reason: {log.details.reason}
                              </span>
                            )}
                          </div>
                        )}
                        
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            <span>{log.user_name || log.user_email || 'Unknown'}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>{formatDate(log.timestamp)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-slate-700 mb-2">
                No Audit Logs Found
              </h3>
              <p className="text-slate-500">
                No actions match your current filters.
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Page {pagination.page} of {pagination.pages} ({pagination.total} total)
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => loadLogs(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="rounded-full"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                onClick={() => loadLogs(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="rounded-full"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default AuditLogsPage;
